import { useState, useRef } from "react";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { useGetUserInfo } from "hooks/useGetUserInfo";

import { Tabs, Card, Spin, Alert, Avatar, Tag, Button, Dropdown, Menu, notification } from "antd";
import {
    BarChart3,
    Users,
    Target,
    TrendingUp,
    Briefcase,
    Clock,
    Award,
    PieChart,
    Download,
    Filter,
    MoreVertical,
    RefreshCw
} from "lucide-react";
import { DashboardOverview } from "./components/DashboardOverview";
import { RecruitmentMetrics } from "./components/RecruitmentMetrics";
import { TeamManagement } from "./components/TeamManagement";
import { DashboardMetrics } from "./types";
import { useGetEnterpriseDashboard } from "./hooks/useGetEnterpriseDashboard";
import { ApplicationTrends } from "./components/ApplicationTrends";
import { DiversityMetrics } from "./components/DiversityMetrics";
import { RecruiterPerformance } from "./components/RecruiterPerformance";
import { TalentEngagement } from "./components/TalentEngagement";
import { PipelineStatus } from "./components/PipelineStatus";
import * as XLSX from "xlsx";

const { TabPane } = Tabs;

export const EnterpriseDashboard = () => {
    const { userInfo } = useGetUserInfo();
    const { data, isLoading, error, refetch } = useGetEnterpriseDashboard();
    const [activeTab, setActiveTab] = useState<string>("overview");
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);
    const [exporting, setExporting] = useState(false);

    if (isLoading) {
        return (
            <DashboardLayout>
                <div className="flex flex-col items-center justify-center min-h-screen">
                    <Spin size="large" />
                    <p className="mt-4 text-gray-600">Loading dashboard data...</p>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <div className="p-6">
                    <Alert
                        message="Error Loading Dashboard"
                        description="Failed to load dashboard data. Please try again later."
                        type="error"
                        showIcon
                        action={
                            <Button size="small" onClick={() => refetch()}>
                                Retry
                            </Button>
                        }
                    />
                </div>
            </DashboardLayout>
        );
    }

    const handleExportDashboard = () => {
        setExporting(true);
        try {
            const workbook = XLSX.utils.book_new();

            // Export quick stats
            const quickStatsData = quickStats.map(stat => ({
                Metric: stat.title,
                Value: stat.value,
                Last_Updated: new Date().toISOString()
            }));
            const ws1 = XLSX.utils.json_to_sheet(quickStatsData);
            XLSX.utils.book_append_sheet(workbook, ws1, "Quick Stats");

            // Export tab data if available
            if (data) {
                const tabData = {
                    "Dashboard Data": new Date().toLocaleDateString(),
                    "Total Job Postings": data.recruitment_activity?.total_job_postings || 0,
                    "Active Job Postings": data.recruitment_activity?.active_job_postings || 0,
                    "Total Hires": data.recruitment_activity?.hires || 0,
                    "Avg Time to Fill": `${data.hiring_metrics?.average_time_to_fill || 0} days`,
                    "Profile Views": data.employer_branding?.profile_views || 0
                };
                const ws2 = XLSX.utils.json_to_sheet([tabData]);
                XLSX.utils.book_append_sheet(workbook, ws2, "Summary");
            }

            XLSX.writeFile(workbook, `Dashboard_Export_${new Date().toISOString().split('T')[0]}.xlsx`);

            notification.success({
                message: 'Export Successful',
                description: 'Dashboard data has been exported to Excel.',
            });
        } catch (err) {
            notification.error({
                message: 'Export Failed',
                description: 'Failed to export dashboard data.',
            });
        } finally {
            setExporting(false);
        }
    };

    const quickStats = [
        {
            title: "Active Jobs",
            value: data?.recruitment_activity?.active_job_postings || 0,
            icon: <Briefcase className="w-5 h-5" />,
            color: "bg-blue-500",
            description: "Currently open positions",
        },
        {
            title: "Total Hires",
            value: data?.recruitment_activity?.hires || 0,
            icon: <Users className="w-5 h-5" />,
            color: "bg-emerald-500",
            description: "This month",
        },
        {
            title: "Avg Time to Fill",
            value: `${data?.hiring_metrics?.average_time_to_fill || 0} days`,
            icon: <Clock className="w-5 h-5" />,
            color: "bg-violet-500",
            description: "Average duration",
        },
        {
            title: "Conversion Rate",
            value: `${data?.quick_stats?.conversion_rate || 0}%`,
            icon: <Target className="w-5 h-5" />,
            color: "bg-amber-500",
            description: "Applications to hire",
        },
        {
            title: "Satisfaction Score",
            value: data?.quick_stats?.satisfaction_score || 0,
            icon: <Award className="w-5 h-5" />,
            color: "bg-rose-500",
            description: "Candidate feedback",
        },
        {
            title: "Profile Views",
            value: data?.employer_branding?.profile_views || 0,
            icon: <TrendingUp className="w-5 h-5" />,
            color: "bg-indigo-500",
            description: "Employer branding",
        },
    ];

    return (
        <DashboardLayout>
            <Navbar
                description={
                    userInfo?.current_team ? userInfo.current_team.name : "Enterprise Dashboard"
                }
                title={`Welcome back, ${userInfo?.name || "User"}`}
            />
            <div className="min-h-screen bg-gray-50">


                <div className="p-6">
                    {/* Quick Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
                        {quickStats.map((stat, index) => (
                            <Card
                                key={index}
                                className="shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer border-0"
                                bodyStyle={{ padding: '16px' }}
                                onClick={() => {
                                    if (stat.title.includes("Jobs")) setActiveTab("recruitment");
                                    if (stat.title.includes("Hires")) setActiveTab("performance");
                                    if (stat.title.includes("Time")) setActiveTab("process");
                                    if (stat.title.includes("Conversion")) setActiveTab("performance");
                                    if (stat.title.includes("Satisfaction")) setActiveTab("engagement");
                                    if (stat.title.includes("Views")) setActiveTab("overview");
                                }}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-500 mb-1">{stat.title}</p>
                                        <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
                                        <p className="text-xs text-gray-400 mb-2">{stat.description}</p>                                        
                                    </div>
                                    <div className={`${stat.color} p-3 rounded-xl flex items-center justify-center ml-4`}>
                                        <div className="text-white">{stat.icon}</div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Dashboard Tabs */}
                    <Card
                        className="shadow-lg border-0 rounded-xl"
                        bodyStyle={{ padding: 0 }}
                        title={
                            <div className="flex items-center justify-between px-6 pt-6">
                                <div className="flex items-center gap-4">
                                    <Tabs
                                        activeKey={activeTab}
                                        onChange={setActiveTab}
                                        type="line"
                                        size="large"
                                        className="dashboard-tabs"
                                    >
                                        <TabPane
                                            tab={
                                                <span className="flex items-center gap-2 px-2">
                                                    <BarChart3 className="w-4 h-4" />
                                                    Overview
                                                </span>
                                            }
                                            key="overview"
                                        />
                                        <TabPane
                                            tab={
                                                <span className="flex items-center gap-2 px-2">
                                                    <Briefcase className="w-4 h-4" />
                                                    Recruitment
                                                </span>
                                            }
                                            key="recruitment"
                                        />
                                        <TabPane
                                            tab={
                                                <span className="flex items-center gap-2 px-2">
                                                    <Users className="w-4 h-4" />
                                                    Talent Engagement
                                                </span>
                                            }
                                            key="engagement"
                                        />
                                        <TabPane
                                            tab={
                                                <span className="flex items-center gap-2 px-2">
                                                    <PieChart className="w-4 h-4" />
                                                    Diversity
                                                </span>
                                            }
                                            key="diversity"
                                        />
                                        <TabPane
                                            tab={
                                                <span className="flex items-center gap-2 px-2">
                                                    <Target className="w-4 h-4" />
                                                    Team Performance
                                                </span>
                                            }
                                            key="performance"
                                        />
                                        <TabPane
                                            tab={
                                                <span className="flex items-center gap-2 px-2">
                                                    <TrendingUp className="w-4 h-4" />
                                                    Trends
                                                </span>
                                            }
                                            key="trends"
                                        />
                                        <TabPane
                                            tab={
                                                <span className="flex items-center gap-2 px-2">
                                                    <Users className="w-4 h-4" />
                                                    Team Management
                                                </span>
                                            }
                                            key="team"
                                        />
                                    </Tabs>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        icon={<Filter className="w-4 h-4" />}
                                        size="middle"
                                        onClick={() => {/* Filter modal logic */ }}
                                    >
                                        Filter
                                    </Button>
                                </div>
                            </div>
                        }
                    >
                        <div className="px-6 pb-6">
                            {activeTab === "overview" && <DashboardOverview data={data as DashboardMetrics} />}
                            {activeTab === "recruitment" && <RecruitmentMetrics data={data as DashboardMetrics} />}
                            {activeTab === "engagement" && <TalentEngagement data={data as DashboardMetrics} />}
                            {activeTab === "diversity" && <DiversityMetrics data={data as DashboardMetrics} />}
                            {activeTab === "performance" && <RecruiterPerformance data={data as DashboardMetrics} />}
                            {activeTab === "trends" && <ApplicationTrends data={data as DashboardMetrics} />}
                            {activeTab === "team" && <TeamManagement data={data as DashboardMetrics} />}
                        </div>
                    </Card>

                    {/* Pipeline Status Widget - Always Visible */}
                    <div className="mt-8">
                        <PipelineStatus data={data as DashboardMetrics} />
                    </div>

                    {/* Data Last Updated */}
                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-500">
                            Data last updated: {new Date().toLocaleString()} •
                            <Button type="link" size="small" onClick={() => refetch()} className="ml-2">
                                Refresh now
                            </Button>
                        </p>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

// Add custom styles
const styles = `
  .dashboard-tabs .ant-tabs-nav {
    margin-bottom: 0;
  }
  .dashboard-tabs .ant-tabs-tab {
    padding: 12px 8px;
    margin: 0 8px;
  }
  .dashboard-tabs .ant-tabs-tab-active {
    font-weight: 600;
  }
`;

export default EnterpriseDashboard;